import { css } from 'lit';
import componentStyles from '../styles/component.styles';
export default css `
  ${componentStyles}
  /**
--------------------------------------------------
* General
--------------------------------------------------
*/
:host {
    display: block;
    background-color: var(--p-bg);
    color: var(--p-text-color);
  }

  :host([dark]) {
    background-color: var(--p-bg-dark);
    color: var(--p-text-color-light);
  }

  .video-page {
    padding: var(--p-gap) 0 var(--p-section-padding);
  }

  .container {
    width: min(var(--p-container-width), 100% - 2 * var(--p-gap));
    margin-inline: auto;
    display: grid;
    grid-template-columns: 3fr 2fr;
    grid-template-areas:
      'live live'
      'player player'
      'heading actions'
      'body actions';
    grid-template-rows: auto auto auto 1fr;
    column-gap: var(--p-gap);
  }

  /* Long unbroken words (URLs, file-name titles) otherwise widen the grid column past the viewport. */
  h1,
  .live-title,
  .body {
    overflow-wrap: anywhere;
  }

  .live-header {
    grid-area: live;
  }

  .player {
    grid-area: player;
  }

  .heading {
    grid-area: heading;
  }

  .body {
    grid-area: body;
  }

  .actions {
    grid-area: actions;
  }

  @media (max-width: 768px) {
    .video-page {
      padding-top: 0;
    }
    .container {
      width: 100%;
      grid-template-columns: 1fr;
      grid-template-areas:
        'live'
        'player'
        'heading'
        'body'
        'actions';
    }
  }

  /**
--------------------------------------------------
* Player
--------------------------------------------------
*/
  .live-header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem 0;
    font-size: 1rem;
  }

  .live-date {
    opacity: var(--p-text-meta-opacity);
  }

  .live-title {
    font-weight: bold;
  }

  @media (max-width: 768px) {
    .live-header {
      padding-inline: var(--p-gap);
    }
  }

  .player {
    max-height: calc(100svh - calc(var(--p-header-height) + var(--p-gap) * 2));
    aspect-ratio: 16/9;
    width: 100%;
    position: relative;
  }

  .mf-livewait {
    padding-top: 0;
  }

  slot[name='player']::slotted(*) {
    width: 100%;
    height: 100%;
    position: absolute;
    left: 0;
    top: 0;
  }

  /**
--------------------------------------------------
* Info
--------------------------------------------------
*/
  .heading,
  .actions {
    padding-top: var(--p-gap);
  }

  h1 {
    font-family: var(--p-h1-font);
    font-size: var(--p-h1-font-size);
    font-weight: var(--p-h1-font-weight);
    font-style: var(--p-h1-font-style);
    line-height: var(--p-h1-line-height);
    text-transform: var(--p-h1-transform);
    letter-spacing: var(--p-h1-letter-spacing);
    margin: 0;
  }

  .meta {
    display: flex;
    gap: 0.5rem;
    align-items: center;
    flex-wrap: wrap;
    margin: 0.5rem 0 1rem;
    font-size: 1rem;
  }

  .meta time {
    opacity: var(--p-text-meta-opacity);
  }

  .meta .viewers {
    display: flex;
    gap: 0.25rem;
    align-items: center;
    font-weight: bold;
  }

  .description {
    max-width: var(--p-text-max-width);
    font-family: var(--p-preamble-font);
    font-size: var(--p-preamble-font-size);
    font-weight: var(--p-preamble-font-weight);
    font-style: var(--p-preamble-font-style);
    line-height: var(--p-preamble-line-height);
    letter-spacing: var(--p-preamble-letter-spacing);
    margin: 0;
    white-space: pre-line;
  }

  a {
    cursor: pointer;
    color: var(--p-link-text-color);
    text-decoration: var(--p-link-text-decoration);
  }

  a:hover {
    color: var(--p-link-text-color-hover);
    text-decoration: var(--p-link-text-decoration-hover);
  }

  .data {
    margin: var(--p-gap) 0 0;
    --p-p-margin-bottom: 0;
  }

  .data slot[name='data']::slotted(dl) {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 1rem;
  }

  @media (max-width: 768px) {
    .heading,
    .body,
    .actions {
      padding-inline: var(--p-gap);
    }

    .data {
      margin: var(--p-gap) 0 0 0;
      max-width: 100%;
    }
  }

  .hide-data .data {
    display: none;
  }

  /**
--------------------------------------------------
* Actions
--------------------------------------------------
*/
  .actions {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
    justify-content: flex-end;
    align-items: flex-start;
    align-content: flex-start;
  }

  @media (max-width: 768px) {
    .actions {
      justify-content: flex-start;
    }
  }

  /**
--------------------------------------------------
* Theater Mode
--------------------------------------------------
*/
  .theater-mode.video-page {
    padding-top: 0;
  }

  .theater-mode .container {
    width: 100%;
  }

  .theater-mode .heading,
  .theater-mode .body {
    padding-inline-start: var(--p-gap);
  }

  .theater-mode .actions {
    padding-inline-end: var(--p-gap);
  }

  .theater-mode .player {
    max-height: calc(100svh - calc(var(--p-header-height)));
  }
`;
